import { KeyboardEvent, useEffect, useState } from 'react'
import { FieldValues, Path, UseFormSetValue } from 'react-hook-form'

type TagInputProps<T extends FieldValues> = {
  name: Path<T>
  label?: string
  setValue: UseFormSetValue<T>
  defaultValue?: string[]
  error?: string
  hintText?: string
  placeholder?: string
}

function TagInput<T extends FieldValues>({
  name,
  label,
  setValue,
  defaultValue = [],
  error,
  hintText,
  placeholder = 'Type a barcode...',
}: TagInputProps<T>) {
  const [tags, setTags] = useState<string[]>(defaultValue)
  const [inputValue, setInputValue] = useState('')

  useEffect(() => {
    setValue(name, tags as any)
  }, [tags, name, setValue])

  const addTag = () => {
    const trimmed = inputValue.trim()
    if (trimmed && !tags.includes(trimmed)) {
      setTags((prev) => [...prev, trimmed])
    }
    setInputValue('')
  }

  const removeTag = (index: number) => {
    setTags((prev) => prev.filter((_, i) => i !== index))
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addTag()
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      setTags((prev) => prev.slice(0, -1))
    }
  }

  return (
    <div className="relative w-full">
      {label && (
        <label className="mb-1 block text-xs font-semibold text-sidebar-primary">
          {label}
        </label>
      )}

      {/* Added tags */}
      <div
        className={`min-h-11 w-full rounded-lg border bg-white px-3 py-2 ${
          error ? 'border-error' : 'border-gray-300'
        }`}
      >
        {tags.length === 0 ? (
          <p className="text-xs text-gray-400">No barcodes added yet</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {tags.map((tag, index) => (
              <span
                key={index}
                className="flex items-center gap-1 rounded bg-sidebar-primary px-2.5 py-1 text-xs font-medium text-white"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(index)}
                  className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-white/20 text-[10px] leading-none hover:bg-white/40"
                  aria-label={`Remove ${tag}`}
                >
                  &times;
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Input row */}
      <div className="mt-2 flex items-center gap-2">
        <div
          className={`flex h-11 flex-1 items-center gap-2 rounded-lg border bg-white px-3 ${
            error ? 'border-error' : 'border-gray-300'
          }`}
        >
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="flex-1 appearance-none bg-transparent text-sm text-primary focus:outline-none"
          />
          {inputValue.trim() ? (
            <span className="flex shrink-0 items-center gap-1 rounded border border-gray-300 bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
              <span className="font-mono">↵</span> Enter
            </span>
          ) : (
            <span className="shrink-0 text-[10px] text-gray-400">
              Press Enter to add
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={addTag}
          disabled={!inputValue.trim()}
          className="h-11 shrink-0 rounded-lg border border-gray-300 bg-white px-4 text-sm font-medium text-sidebar-primary transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          + Add
        </button>
      </div>

      {(error || hintText) && (
        <div>
          {error ? (
            <p className="mt-1 text-xs text-error">{error}</p>
          ) : (
            <p className="mt-1 text-[11px] text-gray-500">{hintText}</p>
          )}
        </div>
      )}
    </div>
  )
}

export default TagInput
